import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The topbar's eyebrow + title pair (markup lines 64-70). The eyebrow is brand
 * orange and truncates to a single line — it carries "{projName} · roster" and
 * "{country} · {region}", both of which can overflow; the title never does.
 *
 * Brain passes an empty eyebrow, which in the source renders an empty div of
 * zero height, so omitting it here is equivalent.
 */
export type PageHeaderProps = {
  /** Small brand-orange line above the title. Optional — Brain has none. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Right-aligned slot — toolbar buttons, filters. */
  actions?: ReactNode;
  /** `h1` for the topbar, `h2` when reused inside a page. */
  as?: "h1" | "h2";
  className?: string;
};

export function PageHeader({
  eyebrow,
  title,
  actions,
  as: Heading = "h1",
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("flex items-center gap-6", className)}>
      <div className="min-w-0">
        {eyebrow !== undefined && eyebrow !== "" && (
          <div className="truncate font-display text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-brand">
            {eyebrow}
          </div>
        )}
        <Heading className="font-display text-[1.6rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
          {title}
        </Heading>
      </div>
      {actions !== undefined && (
        <div className="ml-auto flex shrink-0 items-center gap-[10px]">
          {actions}
        </div>
      )}
    </div>
  );
}
